import React, { useContext } from "react";
import type { EventWrapperProps } from "react-big-calendar";
import { NavLink, type NavLinkProps } from "react-router-dom";

import { faEllipsisV, faSignalStream } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";
import { commitMutation } from "relay-runtime";

import { confirm } from "~/lib/prompts";
import { hexToRGB } from "~/lib/utils";
import { RecordIconSwitchEventWrapper } from "~/meetingRecorder/RecordIcon";
import { environment } from "~/models";
import { AgendaContext, type AgendaContextType, type DayEvent } from "~/nav/panels/Meetings";
import { HideEventMutation, invalidateHiddenEvent } from "~/nav/panels/Meetings/Events";
import { MeetingInfoModalWrapper } from "~/nav/panels/Meetings/MeetingInfoModal";
import { getActiveCalendar } from "~/nav/panels/Meetings/utils";
import { ConnectedIcon } from "~/stream/ConnectedIcon";
import Popup from "~/ui/legacy/Popup";

import MeetingInfoButton from "./MeetingInfoButton";
import { timeBlockTypeIcons } from "./utils";

import type { Events_HideEventMutation as HideEventMutationQuery } from "~/__generated__/Events_HideEventMutation.graphql";
import type { ResponseStatusChoices } from "~/__generated__/MeetingsPanelAttendanceMutation.graphql";

import agendaStyles from "~/nav/panels/Meetings/MeetingsPanel.global.module.css";

function stringifyPercent(v: string | number | undefined) {
	return typeof v === "string" ? v : `${v}%`;
}

type DayEventWrapperProps = EventWrapperProps<DayEvent> & {
	rtl?: boolean; // It called `isRtl` in TS file, but the real prop is `rtl` ¯\_(ツ)_/¯
	onKeyPress: () => void;
	// Not mentioned in EventWrapperProps, but actually passed instead of onCLick in case of all-day events
	onSelect?: (event: DayEvent, e: React.MouseEvent<HTMLElement>) => void;
};

type DayEventContentProps = DayEventWrapperProps & AgendaContextType;

interface DayEventContentState {
	modalOpen: boolean;
}

class DayEventContentWrapped extends React.Component<DayEventContentProps, DayEventContentState> {
	state: DayEventContentState = {
		modalOpen: false,
	};

	onOpenMeetingInfoModal = () => this.setState({ modalOpen: true });

	onCloseMeetingInfoModal = () => this.setState({ modalOpen: false });

	hideEvent = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		const { event } = this.props;
		const message = event.isHidden ? (
			"Showing a recurring event will also show all events in the series."
		) : (
			<>
				<span>
					You can reveal this event by selecting <i>Show hidden meetings</i> in the filters menu&ensp;
					<FontAwesomeIcon icon={faEllipsisV} size="1x" />
					&ensp;at the top of the meetings panel.
				</span>
				<br />
				<br />
				<span>Hiding a recurring event will also hide all events in the series.</span>
			</>
		);

		confirm(message, {
			yesText: event.isHidden ? "Show" : "Hide",
			title: event.isHidden ? "Show selected event again?" : "Hide selected event?",
		}).then(result => {
			if (!result) {
				return;
			}

			commitMutation<HideEventMutationQuery>(environment, {
				mutation: HideEventMutation,
				variables: { guid: this.props.event.guid },
				onCompleted: ({ hideEvent }) => {
					if (hideEvent?.ok) {
						invalidateHiddenEvent(this.props.event.id);
					}
				},
			});
		});
	};

	hideEventPressed = () => {
		this.hideEvent();
		this.onCloseMeetingInfoModal();
	};

	editPressed = () => {
		this.props.openEditModal(this.props.event.guid);
	};

	handleAnswerEventAttendance = (responseStatus: ResponseStatusChoices) => () => {
		const { calendars, event, answerEventAttendance } = this.props;
		const activeCalendar = getActiveCalendar(calendars);
		if (activeCalendar) answerEventAttendance(activeCalendar, event.guid, responseStatus);
	};

	isActive: NavLinkProps["isActive"] = (match, location): boolean => {
		if (Boolean(match)) {
			return true;
		}
		if (!location.pathname.startsWith("/stream/") || !this.props.event.note?.stream.id) {
			return false;
		}
		const { id } = this.props.event.note.stream;
		return location.pathname.includes(id);
	};

	clickHandler = (e: React.MouseEvent<HTMLElement>) => {
		const { onSelect, onClick, event } = this.props;
		if (onSelect) {
			onSelect(event, e);
		} else {
			onClick(e);
		}
	};

	render() {
		const {
			event,
			style,
			className,
			accessors,
			rtl,
			selected,
			label,
			getters,
			continuesEarlier,
			continuesLater,
			onDoubleClick,
			onKeyPress,
		} = this.props;

		const { height, top, width, xOffset } = style || {};

		const { start, end, title, status, hasUnreadNoteContent, noteFinished, timeBlockType } = event;
		const inProgress = !!(start && end && moment().isBetween(start, end) && !noteFinished);
		let duration = 0;
		let startDate = moment(start);
		let endDate = moment(end);
		if (start && end) {
			duration = endDate.diff(startDate, "minutes");
			// TODO: We have to clean setTimeout on unmount. This needs to be better if this component
			// does not refactored.
			// Re-render when meeting ends
			if (moment().isBetween(startDate, endDate)) {
				setTimeout(this.forceUpdate.bind(this), endDate.diff(moment(), "millisecond"));
			}
			// Re-render when meeting starts
			if (moment().isBefore(startDate)) {
				setTimeout(this.forceUpdate.bind(this), startDate.diff(moment(), "millisecond"));
			}
		}
		const tooltip = accessors && accessors.tooltip ? accessors.tooltip(event) : "";
		const userProps =
			getters && getters.eventProp && start && end
				? getters.eventProp(event, startDate.toDate(), endDate.toDate(), selected)
				: {};

		const isInline = duration <= 30 || (duration === 45 && window.innerHeight < 1000);

		let eventLabel = label;

		if (status == "cancelled") eventLabel = "Cancelled";
		else if (isInline) eventLabel = eventLabel && eventLabel.includes(" - ") ? eventLabel.split(" - ")[0] : eventLabel;

		const colorStyles: React.CSSProperties = {};
		if (this.props.event.color) {
			colorStyles["--navItemColor-rgb"] = hexToRGB(this.props.event.color);
			colorStyles["--navItemColor"] = this.props.event.color;
		}

		return (
			<div
				data-tour-interaction-target="event-in-panel"
				style={{
					...userProps.style,
					top: stringifyPercent(top),
					[rtl ? "right" : "left"]: stringifyPercent(xOffset),
					width: stringifyPercent(width),
					height: stringifyPercent(height),
					...colorStyles,
				}}
				className={cx("rbc-event", className, userProps.className, {
					"rbc-event-continues-earlier": continuesEarlier,
					"rbc-event-continues-later": continuesLater,
					"rbc-event-allday": event.isAllDay,
					"event-calendar-inline": isInline || event.isAllDay,
				})}
			>
				<MeetingInfoButton
					event={this.props.event}
					onHide={this.hideEventPressed}
					onEdit={this.editPressed}
					onOpenMeetingInfoModal={this.onOpenMeetingInfoModal}
					handleAnswerEventAttendance={this.handleAnswerEventAttendance}
				/>
				<NavLink
					onClick={this.clickHandler}
					onDoubleClick={onDoubleClick}
					onKeyPress={onKeyPress}
					title={tooltip ? `${label || ""}: ${tooltip}` : undefined}
					to={`/meetings/${this.props.event.guid}/`}
					className={cx("rbc-event-inner event-calendar-view", {
						"event-calendar-inline": isInline,
					})}
					activeClassName="rbc-selected"
					isActive={this.isActive}
				>
					<div className="rbc-event-content">
						{this.props.event.note?.stream.hasExternalUsersOrHasRelatedExternalStreams && (
							<ConnectedIcon style={{ marginRight: "0.25rem" }} />
						)}
						{(timeBlockType == "focusTime" || timeBlockType == "outOfOffice") && (
							<FontAwesomeIcon icon={timeBlockTypeIcons[timeBlockType]} style={{ marginRight: "0.25em" }} />
						)}
						<p className={agendaStyles.eventTitle}>{title || "(no title)"}</p>
						<div className={agendaStyles.indicatorsContainer}>
							<RecordIconSwitchEventWrapper event={event} className={agendaStyles.recordIconSwitch} />
							{this.props.event.hasNoteContent && (
								<span className={cx("rbc-agenda-note-dot", { [agendaStyles.unreadDot]: hasUnreadNoteContent })} />
							)}
						</div>
					</div>{" "}
					{eventLabel && (
						<div
							className={cx("rbc-event-label", {
								"event-in-progress": inProgress,
								"empty-event": !this.props.event.hasNoteContent,
							})}
						>
							{inProgress && (
								<Popup
									inverted
									position="top center"
									size="tiny"
									trigger={<FontAwesomeIcon icon={faSignalStream} />}
									content="Meeting in progress"
								/>
							)}
							{eventLabel}
						</div>
					)}
				</NavLink>
				{this.state.modalOpen && (
					<MeetingInfoModalWrapper
						eventGuid={event.guid}
						onClose={this.onCloseMeetingInfoModal}
						hideEventPressed={this.hideEventPressed}
					/>
				)}
			</div>
		);
	}
}

export function DayEventContent(props: EventWrapperProps<DayEvent>) {
	const contextProps = useContext(AgendaContext);

	// NOTE: onKeyPressed missing in EventWrapperProps of react-big-calendar, but actually it presented
	return <DayEventContentWrapped {...(props as DayEventWrapperProps)} {...contextProps} />;
}
